"use client"

import React, { useState, useEffect, useCallback } from "react";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    useParams,
    useNavigate,
    Link
} from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Avatar,
    Paper,
    IconButton,
    Chip,
    Divider,
    createTheme, // Re-added createTheme
    ThemeProvider, // Re-added ThemeProvider
} from "@mui/material";
import {
    ArrowBackOutlined,
    PhoneOutlined,
    MailOutlined,
    LocationOnOutlined,
    CalendarTodayOutlined,
    StarOutline,
    EmojiEventsOutlined,
    BusinessOutlined,
    PeopleOutlined,
    TrendingUpOutlined,
    MessageOutlined,
    PersonAddOutlined,
    PublicOutlined,
    SchoolOutlined,
    VerifiedOutlined,
    BlockOutlined,
    TaskAltOutlined,
    ShareOutlined,
    BookmarkBorderOutlined,
    Bookmark
} from "@mui/icons-material";

// All colors are now direct constants.
const primaryMain = '#0d2561';
const primaryDark = '#0a1e4a';
const secondaryMain = '#f0f2f5';
const accentMain = '#00838f';
const accentDark = '#006870';
const errorMain = '#d32f2f';
const warningMain = '#ed6c02';
const warningDark = '#b54708';
const successMain = '#28a745';
const successDark = '#1e7e34';
const infoMain = '#1976d2';
const infoDark = '#13519c';
const textPrimary = '#212529'; // Dark text color (for active tabs/foreground)
const textSecondary = '#6c757d'; // Muted foreground text color
const backgroundDefault = '#ffffff'; // White background (for active tabs, general paper)
const backgroundPaper = '#ffffff'; // Card/Paper background (explicitly white)
const backgroundMuted = '#F8F9FA'; // Light grey background (for tab container)
const yellowStar = '#ffc107'; // Explicit color for yellow stars
const greyStar = '#bdbdbd'; // Explicit color for grey stars


// Define the new lists for Seeking, Offering, and Exploring
const seekingOptions = [
    "Affordable Housing", "Buyers", "Commercial Real Estate", "Developments Existing",
    "Developments New", "Energy Efficiency", "Environmental Hazards", "Flood Insurance for High Risk Areas",
    "Green Property", "Loans Commercial", "Loans Residential", "Mortgage Homes",
    "Mortgage Commercial", "Moving", "New Development", "Ownership",
    "Ownership Solutions", "Pre-Listing Needs", "Property Listings", "Rentals",
    "Sellers", "Senior Living Facilities", "Short-Term Rental",
];

const offeringOptions = [
    "Appraisals and Valuations", "Broker Services", "Co-Living Space Insurance", "Commercial Insurance",
    "Cybersecurity", "Drone Inspections", "Escrow Management", "Escrow Services",
    "Escrow Services Digital", "Escrow Services International", "Financial Services", "Flood Insurance",
    "Flood Mitigation", "Flood Risk Assessment", "Green Building Insurance", "Home Auto Umbrella Insurance",
    "Inspection Services", "Lending Services", "Property Appraisals", "Property Inspections",
    "Real Estate Agent Services", "Real Estate Crowdfunding", "Real Estate Investment Trusts (REITs)",
    "Real Estate Marketing", "Real Estate Photography and Virtual Tours", "Real Estate Tech Lending",
    "Real Estate Technology", "Real Estate Valuations", "Settlement Services", "Smart Home Inspections",
    "Title Insurance", "Virtual Closings and Remote Notarizations", "Virtual Reality",
];

const exploringOptions = [
    "Blockchain Lending", "Blockchain Transactions", "Climate Change", "Co-Living Spaces",
    "Commercial Real Estate", "Compliance with E-Signatures", "Cybersecurity", "Development Laws",
    "Facility Regulations", "Insurance Compliance", "Legal Compliance", "Legal Frameworks",
    "Prop Tech Law", "Properties Laws Co-Living", "Properties Laws Commercial", "Properties Laws Residential",
    "Real Estate Compliance", "Real Estate Law", "Regulations", "Regulatory Changes",
    "Tech Integration of Escrow Services",
];

// Define types for member data for better type safety
interface MemberTag {
    text: string;
    highlighted?: boolean;
}

interface MemberData {
    id: number;
    name: string;
    title: string;
    company: string;
    type: string;
    avatar?: string;
    location: string;
    fullAddress: string;
    phone: string;
    email: string;
    website: string;
    licenseNumber: string;
    yearsExperience: number;
    rating: number;
    reviewCount: number;
    connectionCount: number;
    completedDeals: number;
    tasksAssigned: number;
    specializations: string[];
    serviceAreas: string[];
    bio: string;
    exploring: MemberTag[];
    seeking: MemberTag[];
    offering: MemberTag[];
}

// Sample member data with all photos and new attributes
const allMembersData: { [key: number]: MemberData } = { // Explicit index signature
    1: {
        id: 1,
        name: "Sarah Johnson",
        title: "Senior Real Estate Attorney",
        company: "Johnson Legal Group",
        type: "Attorney",
        avatar: "", // Corrected URL
        location: "Scottsdale, AZ",
        fullAddress: "12345 Anystreet Rd., Scottsdale, AZ 85251",
        phone: "(480) 555-0123",
        email: "sarah.johnson@johnsonlegal.com",
        website: "www.johnsonlegal.com", // Corrected URL
        licenseNumber: "AZ-ATT-12345",
        yearsExperience: 12,
        rating: 4.8,
        reviewCount: 47,
        connectionCount: 156,
        completedDeals: 234,
        tasksAssigned: 8,
        specializations: ["Real Estate Law", "Contract Review", "Title Issues", "Closing Services"],
        serviceAreas: ["Scottsdale", "Phoenix", "Tempe", "Mesa", "Chandler"],
        bio: "Experienced real estate attorney with over 12 years of practice in Arizona. Specializing in residential and commercial real estate transactions, contract negotiations, and title issue resolution. Committed to providing exceptional legal services to buyers, buyers, and real estate professionals.", // Changed bio slightly for testing
        exploring: exploringOptions.map((text) => ({ text })),
        seeking: seekingOptions.map((text) => ({ text })),
        offering: offeringOptions.map((text) => ({ text })),
    },
    2: {
        id: 2,
        name: "Michael Chen",
        title: "Senior Real Estate Broker",
        company: "Chen Realty Partners",
        type: "Broker",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face", // Corrected URL
        location: "Scottsdale, AZ",
        fullAddress: "12345 Anystreet Rd., Scottsdale, AZ 85251",
        phone: "(480) 555-0124",
        email: "michael.chen@chenrealty.com",
        website: "www.chenrealty.com", // Corrected URL
        licenseNumber: "AZ-BRK-12346",
        yearsExperience: 8,
        rating: 4.6,
        reviewCount: 32,
        connectionCount: 89,
        completedDeals: 156,
        tasksAssigned: 5,
        specializations: ["Residential Sales", "Investment Properties", "Market Analysis", "Buyer Representation"],
        serviceAreas: ["Scottsdale", "Phoenix", "Paradise Valley", "Fountain Hills"],
        bio: "Dedicated real estate broker with extensive experience in the Arizona market. Specializing in luxury residential properties and investment opportunities.",
        exploring: exploringOptions.map((text) => ({ text })),
        seeking: seekingOptions.map((text) => ({ text })),
        offering: offeringOptions.map((text) => ({ text })),
    },
    3: {
        id: 3,
        name: "Emily Rodriguez",
        title: "Certified Real Estate Appraiser",
        company: "Rodriguez Appraisal Services",
        type: "Appraiser",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face", // Corrected URL
        location: "Scottsdale, AZ",
        fullAddress: "12345 Anystreet Rd., Scottsdale, AZ 85251",
        phone: "(480) 555-0125",
        email: "emily.rodriguez@rodriguezappraisal.com",
        website: "www.rodriguezappraisal.com", // Corrected URL
        licenseNumber: "AZ-APP-12347",
        yearsExperience: 10,
        rating: 4.9,
        reviewCount: 28,
        connectionCount: 67,
        completedDeals: 89,
        tasksAssigned: 3,
        specializations: ["Residential Appraisal", "Commercial Appraisal", "Market Analysis", "Property Valuation"],
        serviceAreas: ["Scottsdale", "Phoenix", "Tempe", "Mesa"],
        bio: "Professional real estate appraiser with a decade of experience providing accurate property valuations for residential and commercial properties.",
        exploring: exploringOptions.map((text) => ({ text })),
        seeking: seekingOptions.map((text) => ({ text })),
        offering: offeringOptions.map((text) => ({ text })),
    },
    11: {
        id: 11,
        name: "Thomas Garcia",
        title: "Real Estate Broker",
        company: "Garcia Brokerage",
        type: "Broker",
        avatar: "https://images.unsplash.com/photo-1560250097042-b8d87734a5a2?w=150&h=150&fit=crop&crop=face", // Corrected URL
        location: "Scottsdale, AZ",
        fullAddress: "12345 Anystreet Rd., Scottsdale, AZ 85251",
        phone: "(480) 555-0135",
        email: "thomas.garcia@garciabrokerage.com",
        website: "www.garciabrokerage.com", // Corrected URL
        licenseNumber: "AZ-BRK-12357",
        yearsExperience: 15,
        rating: 4.7,
        reviewCount: 45,
        connectionCount: 123,
        completedDeals: 278,
        tasksAssigned: 12,
        specializations: ["Commercial Real Estate", "Investment Properties", "Property Management", "Market Analysis"],
        serviceAreas: ["Scottsdale", "Phoenix", "Tempe", "Mesa", "Chandler"],
        bio: "Experienced commercial real estate broker with over 15 years in the industry. Specializing in investment properties and commercial transactions.",
        exploring: exploringOptions.map((text) => ({ text })),
        seeking: seekingOptions.map((text) => ({ text })),
        offering: offeringOptions.map((text) => ({ text })),
    },
    12: {
        id: 12,
        name: "Rebecca Taylor",
        title: "Senior Real Estate Appraiser",
        company: "Taylor Appraisals",
        type: "Appraiser",
        avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=face", // Corrected URL
        location: "Scottsdale, AZ",
        fullAddress: "12345 Anystreet Rd., Scottsdale, AZ 85251",
        phone: "(480) 555-0136",
        email: "rebecca.taylor@taylorappraisals.com",
        website: "www.taylorappraisals.com", // Corrected URL
        licenseNumber: "AZ-APP-12358",
        yearsExperience: 12,
        rating: 4.8,
        reviewCount: 38,
        connectionCount: 95,
        completedDeals: 167,
        tasksAssigned: 7,
        specializations: ["Residential Appraisal", "Luxury Properties", "Estate Valuation", "Market Analysis"],
        serviceAreas: ["Scottsdale", "Paradise Valley", "Phoenix", "Fountain Hills"],
        bio: "Senior real estate appraiser specializing in luxury residential properties and estate valuations with over 12 years of experience.",
        exploring: exploringOptions.map((text) => ({ text })),
        seeking: seekingOptions.map((text) => ({ text })),
        offering: offeringOptions.map((text) => ({ text })),
    },
};

interface EducationData {
    degree: string;
    school: string;
    year: string;
}

const educationData: EducationData[] = [
    { degree: "Juris Doctor", school: "Arizona State University Sandra Day O'Connor College of Law", year: "2012" },
    { degree: "Bachelor of Arts in Political Science", school: "University of Arizona", year: "2009" },
];

const certificationsData: string[] = [
    "Arizona State Bar Association", "American Bar Association - Real Property Section", "Certified Real Estate Attorney",
];

interface ConnectionData {
    id: number;
    name: string;
    type: string;
    avatar: string;
    company: string;
}

const connectionsData: ConnectionData[] = [
    { id: 2, name: "Michael Chen", type: "Broker", avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face", company: "Chen Realty Partners" },
    { id: 3, name: "Emily Rodriguez", type: "Appraiser", avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face", company: "Rodriguez Appraisal Services" },
    { id: 4, name: "David Thompson", type: "Inspector", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face", company: "Thompson Inspection Co." },
    { id: 5, name: "Lisa Wang", type: "Lender", avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face", company: "First Capital Lending" },
];

interface RecentActivityData {
    type: "deal" | "review" | "connection";
    description: string;
    date: string;
}

const recentActivityData: RecentActivityData[] = [
    { type: "deal", description: "Completed closing for $850K residential property", date: "2024-01-10" },
    { type: "review", description: "Received 5-star review from Michael Chen", date: "2024-01-08" },
    { type: "connection", description: "Connected with 3 new real estate professionals", date: "2024-01-05" },
];

interface ReviewData {
    id: number;
    reviewer: string;
    reviewerType: string;
    rating: number;
    comment: string;
    date: string;
}

const reviewsData: ReviewData[] = [
    {
        id: 1, reviewer: "Michael Chen", reviewerType: "Broker", rating: 5,
        comment: "Sarah provided exceptional legal support throughout our transaction. Her attention to detail and quick response time made the closing process seamless.",
        date: "2024-01-08",
    },
    {
        id: 2, reviewer: "Emily Rodriguez", reviewerType: "Appraiser", rating: 5,
        comment: "Professional, knowledgeable, and always available when needed. I highly recommend Sarah for any real estate legal matters.",
        date: "2023-12-15",
    },
    {
        id: 3, reviewer: "David Thompson", reviewerType: "Inspector", rating: 4,
        comment: "Great experience working with Sarah. She handled complex title issues efficiently and kept all parties informed throughout the process.",
        date: "2023-12-02",
    },
];

// Helper component for Material-UI Tabs to display content for each tab.
function CustomTabPanel(props: React.PropsWithChildren<{ value: number; index: number; }>) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
        </div>
    );
}

// Function to generate a11y props for tabs (still useful even with custom buttons)
function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

// Props interface for MemberProfile component
interface MemberProfileProps {
    memberId: string;
    isContact?: boolean;
    section?: string;
    onBack: () => void;
}

// MemberProfile component, now consuming theme via useTheme hook
export default function MemberProfile({
    memberId,
    isContact = false,
    section = "members",
    onBack
}: MemberProfileProps) {
    // const theme = useTheme(); // Theme is not used in this component's direct styling

    const [activeTab, setActiveTab] = useState(0); // 0 for Overview, 1 for Activity, etc.
    const [isRequestSent, setIsRequestSent] = useState(false);
    const [isBookmarked, setIsBookmarked] = useState(false);

    const memberData: MemberData = allMembersData[Number.parseInt(memberId)] || allMembersData[1];

    useEffect(() => {
        if (memberId) {
            const storedBookmark = localStorage.getItem(`member_favorite_${memberId}`);
            if (storedBookmark !== null) {
                setIsBookmarked(JSON.parse(storedBookmark));
            }
            const pendingConnectId = localStorage.getItem("pending_connect_id");
            if (pendingConnectId && Number.parseInt(pendingConnectId, 10) === memberData.id) {
                setIsRequestSent(true);
            }
        }
    }, [memberId, memberData.id]);

    const handleConnect = useCallback(() => {
        if (!isRequestSent) {
            setIsRequestSent(true);
            if (memberId) {
                localStorage.setItem("pending_connect_id", memberId.toString());
            }
            console.log("Connection request sent for member:", memberId);
        }
    }, [isRequestSent, memberId]);

    const handleDelete = () => {
        console.log("Delete/Block member:", memberId);
    };

    const handleShare = () => {
        const dummyElement = document.createElement('textarea');
        dummyElement.value = window.location.href;
        document.body.appendChild(dummyElement);
        dummyElement.select();
        document.execCommand('copy');
        document.body.removeChild(dummyElement);
        console.log("Profile link copied to clipboard!");
    };

    const handleBookmark = () => {
        const newBookmarkStatus = !isBookmarked;
        setIsBookmarked(newBookmarkStatus);
        if (memberId) {
            localStorage.setItem(`member_favorite_${memberId}`, JSON.stringify(newBookmarkStatus));
        }
        console.log(`Member ${memberId} bookmark status: ${newBookmarkStatus}`);
    };

    const renderActionButtons = useCallback(() => {
        if (section === "members") {
            return (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        onClick={handleConnect}
                        disabled={isRequestSent}
                        startIcon={<PersonAddOutlined />}
                        sx={{
                            textTransform: 'none',
                            borderRadius: 1,
                            bgcolor: isRequestSent ? warningMain : accentMain, // Hardcoded colors
                            '&:hover': {
                                bgcolor: isRequestSent ? warningDark : accentDark, // Hardcoded colors
                            },
                        }}
                    >
                        {isRequestSent ? "Request Sent" : "Connect"}
                    </Button>
                    <Button variant="outlined" startIcon={<MessageOutlined />} sx={{ textTransform: 'none', borderRadius: 1, color:'black',borderColor:'#e2e8f0','&:hover': {
                                bgcolor: accentDark, // Hardcoded colors
                                color:'#fff'
                            } }}>
                        Message
                    </Button>
                </Box>
            );
        } else { // Handles "pending" and "contacts" sections
            return (
                <Box sx={{ display: 'flex', gap: 2,  }}>
                    <Button
                        variant="contained"
                        startIcon={<PersonAddOutlined />}
                        sx={{
                            textTransform: 'none',
                            borderRadius: 1,
                            bgcolor: successMain, // Connected state green background
                            '&:hover': {
                                bgcolor: successDark,
                            },
                        }}
                    >
                        Connected
                    </Button>
                    <Button variant="outlined" startIcon={<MessageOutlined />} sx={{ textTransform: 'none', borderRadius: 1 }}>
                        Message
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<BlockOutlined />}
                        onClick={handleDelete}
                        sx={{
                            textTransform: 'none',
                            borderRadius: 1,
                            color: errorMain,
                            borderColor: errorMain,
                            bgcolor: 'transparent',
                            '&:hover': {
                                bgcolor: 'rgba(211, 47, 47, 0.04)',
                                borderColor: errorMain,
                            },
                        }}
                    >
                        Block
                    </Button>
                </Box>
            );
        }
    }, [isRequestSent, section, handleConnect, handleDelete]); // Removed theme from dependency array

    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            {/* Top Bar with Back Navigation and Share/Bookmark */}
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 0, border:'none',boxShadow:'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between',background:'none', marginTop:'50px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}> {/* Added flexGrow: 1 */}
                    {/* Link to go back to MyNetworkPage */}
                    <Button
                        component={Link} // Use Link component from react-router-dom
                        to="/mynetwork" // Link to the root path which displays MyNetworkPage
                        onClick={onBack} // Call onBack prop (simulates router.back())
                        sx={{
                            textTransform: 'none',
                            color: primaryMain, // Blue color from screenshot
                            '&:hover': {
                                color: primaryDark,
                                textDecoration: 'underline',
                                bgcolor: 'transparent',
                            },
                            padding: '6px 8px', // Adjust padding for button appearance
                        }}
                    >
                        <ArrowBackOutlined sx={{ mr: 1, fontSize: '1.2rem' }} /> {/* Icon with right margin */}
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>Back to Network</Typography> {/* Text with font styling */}
                    </Button>
                </Box>
                
            </Paper>

            {/* Main Content Area */}
            <Box component="main" sx={{ flexGrow: 1, p: 3, pt: 0 }}>
                <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                    {/* Profile Header */}
                    
                    <Box sx={{ position:'relative', display: "flex", flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'center', md: 'flex-start' }, gap: 4, mb: 3  }}>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', md: { alignItems: 'flex-start' } }}>
                            <Avatar
                                sx={{
                                    width: 120,
                                    height: 120,
                                    flexShrink: 0,
                                    borderRadius: '50%',
                                    mb: 2
                                }}
                            >
                                <img src={memberData.avatar} alt={memberData.name} style={{ borderRadius: '50%', width: '100%', height: '100%', objectFit: 'cover',display:memberData.avatar ? 'inline' : 'none' }} />
                                {!memberData.avatar && (
                                    <Typography variant="h4" sx={{ textTransform: 'uppercase', fontWeight: 'bold', color: textSecondary }}>
                                        {memberData.name.split(" ").map((n: string) => n[0]).join("")}
                                    </Typography>
                                )}
                            </Avatar>
                            {renderActionButtons()}
                        </Box>

                        <Box sx={{ flexGrow: 1, textAlign: { xs: 'center', md: 'left' } }}>
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: { xs: 'center', md: 'flex-start' }, justifyContent: 'space-between', flexDirection: { xs: 'column', md: 'row' } }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: primaryMain, mb: 0.5 }}>
                                            {memberData.name}
                                        </Typography>
                                        <Typography variant="h5" sx={{ color: textSecondary, mb: 1 }}>
                                            {memberData.title}
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'medium', color: primaryMain }}>
                                            {memberData.company}
                                        </Typography>
                                        <Chip label={memberData.type} variant="outlined" sx={{ mt: 1, borderColor: '#e2e8f0', color: '#020817',fontWeight:600,height:'20px', backgroundColor:'#eaf0f5cc' }} />
                                    </Box>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2, mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', color: textSecondary }}>
                                    <LocationOnOutlined sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                                    <Typography variant="body1">{memberData.location}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', color: textSecondary }}>
                                    <PhoneOutlined sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                                    <Typography variant="body1">{memberData.phone}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', color: textSecondary }}>
                                    <MailOutlined sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                                    <Typography variant="body1">{memberData.email}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', color: textSecondary }}>
                                    <PublicOutlined sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                                    <Typography variant="body1">
                                        <a href={memberData.website} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                            {memberData.website}
                                        </a>
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, fontSize: '0.875rem' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <StarOutline sx={{ fontSize: '1rem', color: yellowStar }} />
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{memberData.rating}</Typography>
                                    <Typography variant="body2" sx={{ color: textSecondary, ml: 0.5 }}>({memberData.reviewCount} reviews)</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PeopleOutlined sx={{ fontSize: '1rem', color: primaryMain }} />
                                    <Typography variant="body2">{memberData.connectionCount} connections</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <TrendingUpOutlined sx={{ fontSize: '1rem', color: successMain }} />
                                    <Typography variant="body2">{memberData.completedDeals} completed deals</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarTodayOutlined sx={{ fontSize: '1rem', color: primaryMain }} />
                                    <Typography variant="body2">{memberData.yearsExperience} years experience</Typography>
                                </Box>
                                {/* This is the 8 tasks assigned quick stat */}
                                {isContact && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <TaskAltOutlined sx={{ fontSize: '1rem', color: accentMain }} />
                                        <Typography variant="body2">{memberData.tasksAssigned} tasks assigned</Typography>
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ position:'absolute',top:0,right:0}}>
                                <IconButton onClick={handleShare} aria-label="Share profile" sx={{
                                    border: 'none', // Always a transparent border to prevent layout shift
                                    backgroundColor: 'none', // Normal state: white background
                                    borderRadius: '6px',
                                    transition: 'background-color 0.3s, border-color 0.3s, color 0.3s', // Added border-color transition
                                    '&:hover': {
                                        backgroundColor: '#00838f', // Hover state: green background
                                        borderColor: 'black', // Hover state: black border
                                        '& .MuiSvgIcon-root': {
                                            color: 'white', // Hover state: white icon
                                            fill: 'currentColor', // Hover state: solid white icon
                                        },
                                    },
                                    '& .MuiSvgIcon-root': {
                                        
                                        color:'#0d2561',
                                    
                                        
                                    },
                                    '&.Mui-disabled': {
                                        pointerEvents: 'none',
                                        opacity: 0.5,
                                    }
                                    }}> {/* Set color to black */}
                                                    <ShareOutlined />
                                </IconButton>
                                <IconButton onClick={handleBookmark} aria-label="Bookmark profile" sx={{
                                    border: 'none', // Always a transparent border to prevent layout shift
                                    backgroundColor: 'none', // Normal state: white background
                                    borderRadius: '6px',
                                    transition: 'background-color 0.3s, border-color 0.3s, color 0.3s', // Added border-color transition
                                    '&:hover': {
                                        backgroundColor: '#00838f', // Hover state: green background
                                        borderColor: 'black', // Hover state: black border
                                        '& .MuiSvgIcon-root': {
                                            color: 'white', // Hover state: white icon
                                            fill: 'currentColor', // Hover state: solid white icon
                                        },
                                    },
                                    '& .MuiSvgIcon-root': {
                                        
                                        color:'#0d2561',
                                    
                                        
                                    },
                                    '&.Mui-disabled': {
                                        pointerEvents: 'none',
                                        opacity: 0.5,
                                    }
                                    }}> {/* Set color to black */}
                                    {isBookmarked ? <Bookmark sx={{ color: accentMain }} /> : <BookmarkBorderOutlined />} {/* Bookmark with accentMain color when filled */}
                                </IconButton>
                            </Box>
                        </Box>
                        
                        
                    </Box>
                    

                    {/* About Section - moved inside CustomTabPanel for Overview */}
                    {/* The original position was here, now removed. */}
                </Paper>

                {/* Custom Tabs container styled to match My Network */}
                <Box
                    sx={{
                        borderRadius: '6px',
                        overflow: 'hidden',
                        mb: 3,
                        bgcolor: '#d8e3ed',
                        p: '4px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(5, 1fr)',
                        height: '40px',
                        alignItems: 'center',
                        width: '100%',
                        gap: '4px',
                    }}
                >
                    {/* Custom Tab Button for Overview */}
                    <Button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 0}
                        onClick={() => setActiveTab(0)}
                        sx={{
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            borderRadius: '4px',
                            px: '12px',
                            py: '6px',
                            fontSize: '0.875rem',
                            fontWeight: activeTab === 0 ? 'bold' : 'normal',
                            transition: 'background-color 0.3s, color 0.3s, box-shadow 0.3s',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: activeTab === 0 ? textPrimary : textSecondary,
                            backgroundColor: activeTab === 0 ? backgroundDefault : 'transparent',
                            boxShadow: activeTab === 0 ? '0px 1px 2px rgba(0, 0, 0, 0.1)' : 'none',
                            '&:hover': {
                                backgroundColor: '#e9ecef',
                                ...(activeTab === 0 && {
                                    backgroundColor: backgroundDefault,
                                    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
                                }),
                            },
                            minWidth: 'unset',
                            textTransform: 'none',
                        }}
                    >
                        Overview
                    </Button>
                    {/* Custom Tab Button for Connections */}
                    <Button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 2}
                        onClick={() => setActiveTab(2)}
                        sx={{
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            borderRadius: '4px',
                            px: '12px',
                            py: '6px',
                            fontSize: '0.875rem',
                            fontWeight: activeTab === 2 ? 'bold' : 'normal',
                            transition: 'background-color 0.3s, color 0.3s, box-shadow 0.3s',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: activeTab === 2 ? textPrimary : textSecondary,
                            backgroundColor: activeTab === 2 ? backgroundDefault : 'transparent',
                            boxShadow: activeTab === 2 ? '0px 1px 2px rgba(0, 0, 0, 0.1)' : 'none',
                            '&:hover': {
                                backgroundColor: '#e9ecef',
                                ...(activeTab === 2 && {
                                    backgroundColor: backgroundDefault,
                                    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
                                }),
                            },
                            minWidth: 'unset',
                            textTransform: 'none',
                        }}
                    >
                        Connections
                    </Button>
                     {/* Custom Tab Button for Experience */}
                    <Button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 3}
                        onClick={() => setActiveTab(3)}
                        sx={{
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            borderRadius: '4px',
                            px: '12px',
                            py: '6px',
                            fontSize: '0.875rem',
                            fontWeight: activeTab === 3 ? 'bold' : 'normal',
                            transition: 'background-color 0.3s, color 0.3s, box-shadow 0.3s',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: activeTab === 3 ? textPrimary : textSecondary,
                            backgroundColor: activeTab === 3 ? backgroundDefault : 'transparent',
                            boxShadow: activeTab === 3 ? '0px 1px 2px rgba(0, 0, 0, 0.1)' : 'none',
                            '&:hover': {
                                backgroundColor: '#e9ecef',
                                ...(activeTab === 3 && {
                                    backgroundColor: backgroundDefault,
                                    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
                                }),
                            },
                            minWidth: 'unset',
                            textTransform: 'none',
                        }}
                    >
                        Experience
                    </Button>

                    {/* Custom Tab Button for Activity */}
                    <Button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 1}
                        onClick={() => setActiveTab(1)}
                        sx={{
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            borderRadius: '4px',
                            px: '12px',
                            py: '6px',
                            fontSize: '0.875rem',
                            fontWeight: activeTab === 1 ? 'bold' : 'normal',
                            transition: 'background-color 0.3s, color 0.3s, box-shadow 0.3s',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: activeTab === 1 ? textPrimary : textSecondary,
                            backgroundColor: activeTab === 1 ? backgroundDefault : 'transparent',
                            boxShadow: activeTab === 1 ? '0px 1px 2px rgba(0, 0, 0, 0.1)' : 'none',
                            '&:hover': {
                                backgroundColor: '#e9ecef',
                                ...(activeTab === 1 && {
                                    backgroundColor: backgroundDefault,
                                    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
                                }),
                            },
                            minWidth: 'unset',
                            textTransform: 'none',
                        }}
                    >
                        Activity
                    </Button>

                    

                   

                    {/* Custom Tab Button for Reviews */}
                    <Button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 4}
                        onClick={() => setActiveTab(4)}
                        sx={{
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            borderRadius: '4px',
                            px: '12px',
                            py: '6px',
                            fontSize: '0.875rem',
                            fontWeight: activeTab === 4 ? 'bold' : 'normal',
                            transition: 'background-color 0.3s, color 0.3s, box-shadow 0.3s',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: activeTab === 4 ? textPrimary : textSecondary,
                            backgroundColor: activeTab === 4 ? backgroundDefault : 'transparent',
                            boxShadow: activeTab === 4 ? '0px 1px 2px rgba(0, 0, 0, 0.1)' : 'none',
                            '&:hover': {
                                backgroundColor: '#e9ecef',
                                ...(activeTab === 4 && {
                                    backgroundColor: backgroundDefault,
                                    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
                                }),
                            },
                            minWidth: 'unset',
                            textTransform: 'none',
                        }}
                    >
                        Reviews
                    </Button>
                </Box>

                {/* Tab Content Panels */}
                <CustomTabPanel value={activeTab} index={0}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* About Section - moved here */}
                            <Paper sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: primaryMain }}>
                                    About
                                </Typography>
                                <Typography variant="body1" sx={{ color: textSecondary }}>
                                    {memberData.bio}
                                </Typography>
                            </Paper>
                            {memberData.exploring && memberData.exploring.length > 0 && (
                                <Paper sx={{ p: 3, borderRadius: 2 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: primaryMain }}>Exploring</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {memberData.exploring.map((tag: MemberTag, index: number) => (
                                            <Chip
                                                key={index}
                                                label={tag.text}
                                                variant="outlined"
                                                sx={{ borderColor: '#e2e8f0', color: '#020817',fontWeight:600,height:'20px' }}
                                            />
                                        ))}
                                    </Box>
                                </Paper>
                            )}

                            {memberData.seeking && memberData.seeking.length > 0 && (
                                <Paper sx={{ p: 3, borderRadius: 2 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: primaryMain }}>Seeking</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {memberData.seeking.map((tag: MemberTag, index: number) => (
                                            <Chip key={index} label={tag.text} variant="outlined" sx={{ borderColor: '#e2e8f0', color: '#020817',fontWeight:600,height:'20px' }} />
                                        ))}
                                    </Box>
                                </Paper>
                            )}

                            {memberData.offering && memberData.offering.length > 0 && (
                                <Paper sx={{ p: 3, borderRadius: 2 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: primaryMain }}>Offering</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {memberData.offering.map((tag: MemberTag, index: number) => (
                                            <Chip key={index} label={tag.text} variant="outlined" sx={{ borderColor: '#e2e8f0', color: '#020817',fontWeight:600,height:'20px' }} />
                                        ))}
                                    </Box>
                                </Paper>
                            )}

                            <Paper sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: primaryMain }}>Specializations</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {memberData.specializations.map((spec: string, index: number) => (
                                        <Chip key={index} label={spec} variant="outlined" sx={{ borderColor: '#e2e8f0', color: '#020817',fontWeight:600,height:'20px' }} />
                                    ))}
                                </Box>
                            </Paper>

                            <Paper sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: primaryMain }}>Service Areas</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {memberData.serviceAreas.map((area: string, index: number) => (
                                        <Chip key={index} label={area} variant="outlined" icon={<LocationOnOutlined sx={{ fontSize: '0.9rem' }} />} sx={{ borderColor: '#e2e8f0', color: '#020817',fontWeight:600,height:'20px', backgroundColor:'#eaf0f5cc' }} />
                                    ))}
                                </Box>
                            </Paper>

                            {isContact && (
                                <Paper sx={{ p: 3, borderRadius: 2 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: primaryMain, display: 'flex', alignItems: 'center' }}>
                                        <TaskAltOutlined sx={{ mr: 1 }} />
                                        Tasks Assigned to {memberData.name}
                                    </Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>Contract Review</Typography>
                                                <Chip label="Pending" size="small" sx={{ bgcolor: '#ffffff', color: warningDark, border: '1px solid #b54708',fontWeight:500 }} />
                                            </Box>
                                            <Typography variant="body2" sx={{ color: textSecondary, mb: 1 }}>
                                                Review purchase agreement for 123 Main St
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: textSecondary }}>Due: Jan 25, 2024</Typography>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>Title Search</Typography>
                                                <Chip label="Completed" size="small" sx={{ bgcolor: '#ffffff', color: successDark, border: '1px solid #1e7e34',fontWeight:500 }} />
                                            </Box>
                                            <Typography variant="body2" sx={{ color: textSecondary, mb: 1 }}>Complete title search for property</Typography>
                                            <Typography variant="caption" sx={{ color: textSecondary }}>Completed: Jan 18, 2024</Typography>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>Closing Prep</Typography>
                                               <Chip label="In Progress" size="small" sx={{ bgcolor: '#ffffff', color: infoDark, border: '1px solid #13519c',fontWeight:500 }} />
                                            </Box>
                                            <Typography variant="body2" sx={{ color: textSecondary, mb: 1 }}>Prepare closing documents</Typography>
                                            <Typography variant="caption" sx={{ color: textSecondary }}>Due: Jan 30, 2024</Typography>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>Legal Opinion</Typography>
                                                <Chip label="Pending" size="small" sx={{ bgcolor: '#ffffff', color: warningDark, border: '1px solid #b54708',fontWeight:500 }} />
                                            </Box>
                                            <Typography variant="body2" sx={{ color: textSecondary, mb: 1 }}>Provide legal opinion on zoning issues</Typography>
                                            <Typography variant="caption" sx={{ color: textSecondary }}>Due: Feb 5, 2024</Typography>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>Document Filing</Typography>
                                                <Chip label="Completed" size="small" sx={{ bgcolor: '#ffffff', color: successDark, border: '1px solid #1e7e34',fontWeight:500 }} />
                                            </Box>
                                            <Typography variant="body2" sx={{ color: textSecondary, mb: 1 }}>File deed and mortgage documents</Typography>
                                            <Typography variant="caption" sx={{ color: textSecondary }}>Completed: Jan 15, 2024</Typography>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>Property Survey</Typography>
                                                <Chip label="In Progress" size="small" sx={{ bgcolor: '#ffffff', color: infoDark, border: '1px solid #13519c',fontWeight:500 }} />
                                            </Box>
                                            <Typography variant="body2" sx={{ color: textSecondary, mb: 1 }}>Coordinate property boundary survey</Typography>
                                            <Typography variant="caption" sx={{ color: textSecondary }}>Due: Feb 10, 2024</Typography>
                                        </Paper>
                                    </Box>
                                </Paper>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Paper sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: primaryMain, display: 'flex', alignItems: 'center' }}>
                                    <VerifiedOutlined sx={{ mr: 1 }} />
                                    License Information
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box>
                                        <Typography component="span" sx={{ fontWeight: 'medium' }}>License #:</Typography>
                                        <Typography component="span" sx={{ ml: 1, color: textSecondary }}>{memberData.licenseNumber}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography component="span" sx={{ fontWeight: 'medium' }}>Status:</Typography>
                                        <Chip label="Active" size="small" sx={{ ml: 1, bgcolor: '#e8f5e9', color: successDark, borderColor: successMain }} />
                                    </Box>
                                </Box>
                            </Paper>

                            <Paper sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: primaryMain, display: 'flex', alignItems: 'center' }}>
                                    <EmojiEventsOutlined sx={{ mr: 1 }} />
                                    Quick Stats
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body1">Rating</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <StarOutline sx={{ fontSize: '1rem', color: yellowStar, mr: 0.5 }} />
                                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{memberData.rating}/5</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body1">Connections</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{memberData.connectionCount}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body1">Completed Deals</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{memberData.completedDeals}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body1">Experience</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{memberData.yearsExperience} years</Typography>
                                    </Box>
                                    {isContact && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body1">Tasks Assigned</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{memberData.tasksAssigned}</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                        </Box>
                    </Box>
                </CustomTabPanel>

                <CustomTabPanel value={activeTab} index={1}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: primaryMain }}>Recent Activity</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {recentActivityData.map((activity: RecentActivityData, index: number) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <Box sx={{ flexShrink: 0 }}>
                                        {activity.type === "deal" && <BusinessOutlined sx={{ fontSize: '1.25rem', color: successMain }} />}
                                        {activity.type === "review" && <StarOutline sx={{ fontSize: '1.25rem', color: yellowStar }} />}
                                        {activity.type === "connection" && <PeopleOutlined sx={{ fontSize: '1.25rem', color: primaryMain }} />}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2">{activity.description}</Typography>
                                        <Typography variant="caption" sx={{ color: textSecondary }}>
                                            {new Date(activity.date).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                </CustomTabPanel>

                <CustomTabPanel value={activeTab} index={2}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: primaryMain, display: 'flex', alignItems: 'center' }}>
                            <PeopleOutlined sx={{ mr: 1 }} />
                            Connections in common ({connectionsData.length})
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
                            {connectionsData.map((connection: ConnectionData, index: number) => (
                                <Paper
                                    key={connection.id}
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                                    }}
                                >
                                    <Avatar src={connection.avatar} alt={connection.name} sx={{ width: 48, height: 48 }}>
                                        <img src={connection.avatar} alt={connection.name} style={{ borderRadius: '50%', width: '100%', height: '100%', objectFit: 'cover' }} />
                                        {!connection.avatar && (
                                            <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
                                                {connection.name.split(" ").map((n: string) => n[0]).join("")}
                                            </Typography>
                                        )}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        {/* Corrected Link usage for react-router-dom */}
                                        <Button
                                            component={Link} // Use Link component from react-router-dom
                                            to={`/member/${connection.id}?section=members`} // Use 'to' prop for Link
                                            variant="text"
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 'medium',
                                                color: primaryMain,
                                                p: 0,
                                                minWidth: 0,
                                                justifyContent: 'flex-start',
                                                '&:hover': {
                                                    color: primaryDark,
                                                    textDecoration: 'underline',
                                                    bgcolor: 'transparent'
                                                },
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {connection.name}
                                        </Button>
                                        <Typography variant="body2" sx={{ color: textSecondary }}>{connection.type}</Typography>
                                        <Typography variant="caption" sx={{ color: textSecondary }}>{connection.company}</Typography>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    </Paper>
                </CustomTabPanel>

                <CustomTabPanel value={activeTab} index={3}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 3 }}>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: primaryMain, display: 'flex', alignItems: 'center' }}>
                                <SchoolOutlined sx={{ mr: 1 }} />
                                Education
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {educationData.map((edu: EducationData, index: number) => (
                                    <Box key={index} sx={{ borderLeft: '2px solid', borderColor: primaryMain, pl: 2 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>{edu.degree}</Typography>
                                        <Typography variant="body2" sx={{ color: textSecondary }}>{edu.school}</Typography>
                                        <Typography variant="caption" sx={{ color: textSecondary }}>{edu.year}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>

                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: primaryMain, display: 'flex', alignItems: 'center' }}>
                                <EmojiEventsOutlined sx={{ mr: 1 }} />
                                Certifications
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {certificationsData.map((cert: string, index: number) => (
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <VerifiedOutlined sx={{ fontSize: '1.25rem', color: successMain, mr: 1 }} />
                                        <Typography variant="body1">{cert}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Box>
                </CustomTabPanel>

                <CustomTabPanel value={activeTab} index={4}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {reviewsData.map((review: ReviewData, index: number) => (
                            <Paper key={review.id} sx={{ p: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>{review.reviewer}</Typography>
                                        <Typography variant="body2" sx={{ color: textSecondary }}>{review.reviewerType}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {[...Array(5)].map((_: any, i: number) => (
                                            <StarOutline
                                                key={i}
                                                sx={{
                                                    fontSize: '1rem',
                                                    color: i < review.rating ? yellowStar : greyStar,
                                                    fill: i < review.rating ? 'currentColor' : 'none',
                                                }}
                                            />
                                        ))}
                                        <Typography variant="body2" sx={{ ml: 1, color: textSecondary }}>
                                            {new Date(review.date).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Typography variant="body1" sx={{ color: textSecondary }}>{review.comment}</Typography>
                            </Paper>
                        ))}
                    </Box>
                </CustomTabPanel>
            </Box>
        </Box>
    );
}